/**
 * LoginPage — email + password (technicians are staff accounts) with a
 * magic-link alternative. Mirrors the customer portal's mode switch.
 * Portal: On-Site Portal
 */
import { useId, useState, type FormEvent } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { supabase, SUPABASE_URL } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';
import { PORTAL_COLOR, PORTAL_SUBTITLE } from '../config/navigation';

type Mode = 'password' | 'magic-link';

function formatAuthError(message: string | undefined): string {
  if (!SUPABASE_URL) {
    return 'The portal is not connected to Supabase. Set VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY in apps/onsite/.env.local.';
  }
  if (!message || /failed to fetch|networkerror|load failed/i.test(message)) {
    return 'Could not reach the portal backend. Check your connection and try again.';
  }
  if (/invalid login credentials/i.test(message)) return 'Invalid email or password.';
  if (/email not confirmed/i.test(message)) return 'This email address has not been confirmed yet.';
  return message;
}

export function LoginPage() {
  const navigate = useNavigate();
  const { session, loading: authLoading } = useAuth();
  const [mode, setMode] = useState<Mode>('password');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const emailId = useId();
  const passwordId = useId();
  const errorId = useId();

  if (!authLoading && session) return <Navigate to="/dashboard" replace />;

  const switchMode = (next: Mode) => {
    setMode(next);
    setError(null);
    setPassword('');
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      if (mode === 'password') {
        const { error: authError } = await supabase.auth.signInWithPassword({ email: email.trim(), password });
        if (authError) setError(formatAuthError(authError.message));
        else navigate('/dashboard', { replace: true });
      } else {
        const { error: authError } = await supabase.auth.signInWithOtp({
          email: email.trim(),
          options: { emailRedirectTo: window.location.origin },
        });
        if (authError) setError(formatAuthError(authError.message));
        else setSent(true);
      }
    } catch (err) {
      setError(formatAuthError(err instanceof Error ? err.message : undefined));
    } finally {
      setLoading(false);
    }
  };

  const submitDisabled = loading || !email.trim() || (mode === 'password' && !password);

  return (
    <div className="flex min-h-[var(--paz-vh,100vh)] items-center justify-center bg-bg px-4 py-6">
      <div className="w-full max-w-sm">
        <div className="card overflow-hidden shadow-lg">
          <div className="px-6 py-5" style={{ backgroundColor: PORTAL_COLOR }}>
            <img src="/logo.svg" alt="PazNetworks, Inc." className="h-11 w-auto" />
            <p className="mt-1.5 text-[13px] text-white/75">{PORTAL_SUBTITLE}</p>
          </div>

          <div className="px-6 py-5">
            {sent ? (
              <div className="py-2 text-center" role="status">
                <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-primary-soft">
                  <svg className="h-6 w-6 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                </div>
                <p className="text-sm font-medium text-text">Check your email</p>
                <p className="mt-1 text-xs text-text-muted">
                  We sent a sign-in link to <span className="font-medium text-text">{email}</span>. It works on this device only.
                </p>
                <button
                  type="button"
                  onClick={() => {
                    setSent(false);
                    setEmail('');
                  }}
                  className="btn-ghost mt-3 text-xs"
                >
                  Use a different email
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4" noValidate>
                <div>
                  <h1 className="text-lg font-semibold text-text">Sign in</h1>
                  <p className="mt-1 text-sm text-text-muted">
                    {mode === 'password' ? 'Use your PazNetworks staff email and password.' : 'We will email you a one-time sign-in link.'}
                  </p>
                </div>

                <div>
                  <label htmlFor={emailId} className="input-label">
                    Email address
                  </label>
                  <input
                    id={emailId}
                    type="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    autoComplete="username"
                    inputMode="email"
                    className="input-field"
                    aria-describedby={error ? errorId : undefined}
                  />
                </div>

                {mode === 'password' && (
                  <div>
                    <label htmlFor={passwordId} className="input-label">
                      Password
                    </label>
                    <input
                      id={passwordId}
                      type="password"
                      required
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      autoComplete="current-password"
                      className="input-field"
                      aria-describedby={error ? errorId : undefined}
                    />
                  </div>
                )}

                {error && (
                  <div id={errorId} role="alert" className="rounded-md border border-error/40 bg-error-soft px-3 py-2">
                    <p className="text-xs text-error">{error}</p>
                  </div>
                )}

                <button type="submit" disabled={submitDisabled} className="btn-primary w-full">
                  {loading ? (mode === 'password' ? 'Signing in…' : 'Sending…') : mode === 'password' ? 'Sign in' : 'Send sign-in link'}
                </button>

                <p className="text-center">
                  <button type="button" onClick={() => switchMode(mode === 'password' ? 'magic-link' : 'password')} className="btn-ghost text-xs">
                    {mode === 'password' ? 'Email me a sign-in link instead' : 'Sign in with a password instead'}
                  </button>
                </p>
              </form>
            )}
          </div>
        </div>

        <p className="mt-4 text-center text-[11px] text-text-faint">&copy; {new Date().getFullYear()} PazNetworks, Inc. All rights reserved.</p>
      </div>
    </div>
  );
}
